import "./orders.scss";
import * as React from "react";
import { Empty, Table as AntdTable, Tag, Tooltip } from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";
import { EditOutlined, ExportOutlined, InfoCircleOutlined } from "@ant-design/icons";
import moment from "jalali-moment";
import { Badge, UserLink, CopyablePhone, CopyableText } from "components";
import { BadgeType } from "components/badge/badge";
import { Button, buttonType } from "components/button/button";
import { Card } from "components/card/card";
import { ColumnSearch } from "components/column-search/column-search";
import { Page } from "components/page/page";
import { Enum, PaginationList } from "core/domain";
import {
    Order,
    OrderCount,
    OrderFilterValues,
    SortDirection,
} from "core/domain/order/order";
import { digitSperator, faNumToEnNum } from "core/utils/decimalExtentions";
import { downloadCSV } from "core/utils/export-to-file";
import { getIconPath } from "core/utils/getIconPath";
import { jFormat } from "core/utils/jFormat";
import {
    getAllOrders,
    getEditableOrderStates,
    getMarketTypes,
    getOrderSides,
    getOrderSources,
    getOrderState,
    getOrderType,
} from "core/repositories/orders";
import { getTransactionPlatforms } from "core/repositories/transaction";
import { OrderDetailModal } from "./order-detail/order-detail";
import { OrderStateEditModal } from "./edit-state-modal/edit-state-modal";

const initialFilters: OrderFilterValues = {
    marketType: { id: -1, title: "نوع ارز" },
    state: { id: -1, title: "همه وضعیت ها" },
    sort: SortDirection.DESC,
    type: { id: -1, title: "همه سفارش ها" },
    source: { id: -1, title: "همه مبدا ها" },
    platformType: { id: -1, title: "همه پلتفرم‌ها" },
    side: { id: -1, title: "سمت معامله" },
    startDate: moment()
        .locale("fa")
        .subtract(1, "months")
        .format("jYYYY/jMM/jDD"),
};

function buildOrderQuery(filters: OrderFilterValues): string {
    const parameters = new URLSearchParams();

    if (filters.trackingCode)
        parameters.set("trackingCode", filters.trackingCode.trim());
    if (filters.fullName) parameters.set("fullName", filters.fullName.trim());
    if (filters.phoneNumber)
        parameters.set("phoneNumber", faNumToEnNum(filters.phoneNumber.trim()));
    if (filters.minAmount)
        parameters.set("minAmount", faNumToEnNum(filters.minAmount.trim()));
    if (filters.maxAmount)
        parameters.set("maxAmount", faNumToEnNum(filters.maxAmount.trim()));
    if (filters.side.id !== -1) parameters.set("side", String(filters.side.id));
    if (filters.startDate)
        parameters.set("startDate", jFormat(filters.startDate));
    if (filters.endDate) parameters.set("endDate", jFormat(filters.endDate));
    if (filters.marketType?.id !== -1)
        parameters.set("marketType", String(filters.marketType?.id));
    if (filters.source?.id !== -1)
        parameters.set("source", String(filters.source?.id));
    if (filters.type?.id !== -1)
        parameters.set("type", String(filters.type?.id));
    if (filters.state?.id !== -1)
        parameters.set("state", String(filters.state?.id));
    if (filters.market) parameters.set("market", filters.market.trim());
    if (filters.sourceCampaign)
        parameters.set("sourceCampaign", filters.sourceCampaign.trim());
    if (filters.platformType?.id !== -1)
        parameters.set("platformType", String(filters.platformType?.id));
    if (filters.sort !== undefined)
        parameters.set("sort", String(filters.sort));

    const query = parameters.toString();
    return query ? `?${query}` : "";
}

// ستون‌های خروجی اکسل عمداً صریح نوشته شده‌اند تا دقیقاً مثل خروجی قبلی بمانند
// و با تغییر فیلدهای API جابه‌جا یا حذف نشوند. `createdOn` نام قدیمیِ همان `createdAt` است.
function buildOrdersCsvRows(items: Order[]) {
    return items.map((row) => ({
        id: row.id,
        trackingCode: row.trackingCode,
        pair: row.pair,
        sourceQuantity: row.sourceQuantity,
        destinationQuantity: row.destinationQuantity,
        type: row.type,
        createdBy: row.createdBy,
        side: row.side,
        state: row.state,
        createdOn: row.createdAt,
        commission: row.commission,
        marketType: row.marketType,
        source: row.source,
        platform: row.platform,
        phoneNumber: row.phoneNumber,
        baseBalanceAfterOrder: row.baseBalanceAfterOrder,
        quoteBalanceAfterOrder: row.quoteBalanceAfterOrder,
        onPrice: row.onPrice,
        inDollarPrice: row.inDollarPrice,
        inDollarAmount: row.inDollarAmount,
    }));
}

function getOrderStatusBadgeType(stateId: number): BadgeType {
    if (stateId === 1) return "waiting";
    if (stateId === 3) return "success";
    if (stateId === 4 || stateId === 5) return "error";
    return "secondary";
}

export const Orders = () => {
    const [orderList, setOrderList] = React.useState<PaginationList<Order>>();
    const [ordersCount, setOrdersCount] = React.useState<OrderCount>();
    const [cardLoading, setCardLoading] = React.useState(false);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isEditOpen, setIsEditOpen] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState<Order>();
    const [editableOrderStates, setEditableOrderStates] = React.useState<
        Enum[]
    >([]);
    const [pageSize, setPageSize] = React.useState(10);
    const [query, setQuery] = React.useState("");
    const [filterValues, setFilterValues] =
        React.useState<OrderFilterValues>(initialFilters);
    const [marketTypes, setMarketTypes] = React.useState<Enum[]>([
        initialFilters.marketType!,
    ]);
    const [orderStates, setOrderStates] = React.useState<Enum[]>([
        initialFilters.state!,
    ]);
    const [orderSides, setOrderSides] = React.useState<Enum[]>([
        initialFilters.side,
    ]);
    const [orderSources, setOrderSources] = React.useState<Enum[]>([
        initialFilters.source!,
    ]);
    const [orderTypes, setOrderTypes] = React.useState<Enum[]>([
        initialFilters.type!,
    ]);
    const [orderPlatforms, setOrderPlatforms] = React.useState<Enum[]>([
        initialFilters.platformType!,
    ]);

    const filterHandle = React.useCallback(
        (filters: OrderFilterValues = filterValues) => {
            const nextQuery = buildOrderQuery(filters);
            setQuery(nextQuery);
            getAllOrders(
                setCardLoading,
                setOrderList,
                1,
                pageSize,
                nextQuery,
                setOrdersCount,
            );
        },
        [filterValues, pageSize],
    );

    const applyColumnSearch = React.useCallback(
        (patch: Record<string, any>) => {
            const nextFilters = {
                ...filterValues,
                ...patch,
            } as OrderFilterValues;
            setFilterValues(nextFilters);
            filterHandle(nextFilters);
        },
        [filterHandle, filterValues],
    );

    React.useEffect(() => {
        const loadInitialData = async () => {
            // این‌ها فقط گزینه‌های فیلتر را پر می‌کنند و ربطی به دادهٔ جدول ندارند؛
            // اگر setCardLoading را به آن‌ها بدهیم، اسپینر جدول به تعداد همین
            // درخواست‌ها پشت سر هم روشن/خاموش می‌شود و پرش می‌زند.
            const ignoreLoading = () => {};

            getMarketTypes(ignoreLoading, setMarketTypes);
            getOrderState(ignoreLoading, setOrderStates);
            getOrderSides(ignoreLoading, setOrderSides);
            getOrderSources(ignoreLoading, setOrderSources);
            getOrderType(ignoreLoading, setOrderTypes);
            getTransactionPlatforms(ignoreLoading, setOrderPlatforms);
            getEditableOrderStates(ignoreLoading, setEditableOrderStates);

            const initialQuery = buildOrderQuery(initialFilters);
            setQuery(initialQuery);
            getAllOrders(
                setCardLoading,
                setOrderList,
                1,
                10,
                initialQuery,
                setOrdersCount,
            );
        };

        loadInitialData();
    }, []);

    const reloadOrders = React.useCallback(() => {
        getAllOrders(
            setCardLoading,
            setOrderList,
            ordersCount?.pageIndex ?? 1,
            pageSize,
            query,
            setOrdersCount,
        );
    }, [ordersCount?.pageIndex, pageSize, query]);

    const handleTableChange = React.useCallback(
        (
            pagination: TablePaginationConfig,
            _filters: unknown,
            sorter: SorterResult<Order> | SorterResult<Order>[],
        ) => {
            const nextPage = pagination.current ?? 1;
            const nextPageSize = pagination.pageSize ?? pageSize;
            const currentPage = ordersCount?.pageIndex ?? 1;
            const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            const nextSort =
                activeSorter?.columnKey === "createdAt"
                    ? activeSorter.order === "ascend"
                        ? SortDirection.ASC
                        : activeSorter.order === "descend"
                          ? SortDirection.DESC
                          : undefined
                    : filterValues.sort;
            const sortChanged = nextSort !== filterValues.sort;
            const nextFilters = sortChanged
                ? { ...filterValues, sort: nextSort }
                : filterValues;
            const nextQuery = sortChanged
                ? buildOrderQuery(nextFilters)
                : query;
            const requestedPage = sortChanged ? 1 : nextPage;

            if (
                !sortChanged &&
                nextPage === currentPage &&
                nextPageSize === pageSize
            )
                return;

            setPageSize(nextPageSize);
            if (sortChanged) {
                setFilterValues(nextFilters);
                setQuery(nextQuery);
            }
            getAllOrders(
                setCardLoading,
                setOrderList,
                requestedPage,
                nextPageSize,
                nextQuery,
                setOrdersCount,
            );
        },
        [filterValues, ordersCount?.pageIndex, pageSize, query],
    );

    const columns = React.useMemo<ColumnsType<Order>>(
        () => [
            {
                title: "#",
                key: "index",
                width: 70,
                render: (_, __, index) =>
                    ((ordersCount?.pageIndex ?? 1) - 1) * pageSize + index + 1,
            },
            {
                title: (
                    <ColumnSearch
                        title="کاربر"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "fullName",
                                placeholder: "نام و نام خانوادگی",
                            },
                            {
                                key: "phoneNumber",
                                placeholder: "شماره موبایل",
                                dir: "ltr",
                            },
                        ]}
                    />
                ),
                key: "createdBy",
                width: 210,
                render: (_, row) => (
                    <div className="customer-cell">
                        <UserLink userId={row.userId}>
                            <span>{row.createdBy}</span>
                        </UserLink>
                        <small>
                            <CopyablePhone phoneNumber={row.phoneNumber} />
                        </small>
                    </div>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="شناسه"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "trackingCode",
                                placeholder: "شناسه سفارش",
                                dir: "ltr",
                            },
                        ]}
                    />
                ),
                dataIndex: "trackingCode",
                key: "trackingCode",
                width: 175,
                render: (trackingCode: string) => (
                    <CopyableText value={trackingCode} label="شناسه سفارش" />
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="جفت"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "marketType",
                                placeholder: "نوع ارز",
                                type: "select",
                                options: marketTypes,
                            },
                            {
                                key: "market",
                                placeholder: "نام یا نماد ارز",
                                dir: "ltr",
                            },
                        ]}
                    />
                ),
                key: "pair",
                width: 160,
                render: (_, row) => (
                    <div className="pair-cell">
                        <img
                            src={getIconPath(row.pair.base.toLowerCase())}
                            alt=""
                        />
                        <span className="en">{row.pair.base}</span>
                        <span aria-hidden="true"> / </span>
                        <span className="en">{row.pair.quote}</span>
                        <img
                            src={getIconPath(row.pair.quote.toLowerCase())}
                            alt=""
                        />
                    </div>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="خرید/فروش"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "side",
                                placeholder: "سمت معامله",
                                type: "select",
                                options: orderSides,
                            },
                        ]}
                    />
                ),
                dataIndex: "side",
                key: "side",
                width: 100,
                render: (side: Enum) => (
                    <Tag color={side.id === 1 ? "green" : "red"}>
                        {side.title}
                    </Tag>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="پرداخت شده"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "minAmount",
                                placeholder: "حداقل مبلغ",
                                dir: "ltr",
                                htmlType: "number",
                            },
                            {
                                key: "maxAmount",
                                placeholder: "حداکثر مبلغ",
                                dir: "ltr",
                                htmlType: "number",
                            },
                        ]}
                    />
                ),
                dataIndex: "sourceQuantity",
                key: "sourceQuantity",
                align: "left",
                width: 150,
                render: (amount: number, row) => (
                    <div className="value-cell">
                        <span className="en">
                            {row.side.id === 2 ? row.pair.base : row.pair.quote}
                        </span>
                        <span>
                            {row.side.id === 1
                                ? digitSperator(amount.toFixed(0))
                                : digitSperator(amount)}
                        </span>
                    </div>
                ),
            },
            {
                title: "دریافت شده",
                dataIndex: "destinationQuantity",
                key: "destinationQuantity",
                align: "left",
                width: 150,
                render: (amount: number, row) => (
                    <div className="value-cell">
                        <span className="en">
                            {row.side.id === 2 ? row.pair.quote : row.pair.base}
                        </span>
                        <span>
                            {row.side.id === 2
                                ? digitSperator(amount.toFixed(0))
                                : digitSperator(amount)}
                        </span>
                    </div>
                ),
            },
            {
                title: "قیمت واحد (تومان)",
                dataIndex: "onPrice",
                key: "onPrice",
                align: "left",
                width: 145,
                render: (onPrice: number) => digitSperator(onPrice),
            },
            {
                title: (
                    <ColumnSearch
                        title="مبدا"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "source",
                                placeholder: "مبدا سفارش",
                                type: "select",
                                options: orderSources,
                            },
                            {
                                key: "type",
                                placeholder: "نوع سفارش",
                                type: "select",
                                options: orderTypes,
                            },
                            {
                                key: "sourceCampaign",
                                placeholder: "کمپین مبدا",
                            },
                        ]}
                    />
                ),
                dataIndex: "source",
                key: "source",
                width: 125,
                render: (source: Enum) => (
                    <Tag color={source.id === 1 ? "cyan" : "purple"}>
                        {source.title}
                    </Tag>
                ),
            },
            {
                title: (
                    <ColumnSearch
                        title="پلتفرم"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "platformType",
                                placeholder: "پلتفرم",
                                type: "select",
                                options: orderPlatforms,
                            },
                        ]}
                    />
                ),
                dataIndex: "platform",
                key: "platform",
                width: 125,
                render: (platform?: Enum) =>
                    platform ? (
                        <Tag color={platform.id === 1 ? "blue" : "green"}>
                            {platform.title}
                        </Tag>
                    ) : (
                        "-"
                    ),
            },
            {
                title: (
                    <ColumnSearch
                        title="زمان"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "startDate",
                                placeholder: "از تاریخ",
                                type: "date",
                            },
                            {
                                key: "endDate",
                                placeholder: "تا تاریخ",
                                type: "date",
                            },
                        ]}
                    />
                ),
                dataIndex: "createdAt",
                key: "createdAt",
                width: 165,
                sorter: true,
                sortDirections: ["descend", "ascend", "descend"],
                sortOrder:
                    filterValues.sort === SortDirection.ASC
                        ? "ascend"
                        : filterValues.sort === SortDirection.DESC
                          ? "descend"
                          : undefined,
                render: (createdAt: string) =>
                    createdAt ? (
                        <span>
                            {moment
                                .utc(createdAt)
                                .local()
                                .locale("fa")
                                .format("jYYYY/jMM/jDD")}
                            {" - "}
                            {moment.utc(createdAt).local().format("HH:mm")}
                        </span>
                    ) : (
                        "-"
                    ),
            },
            {
                title: (
                    <ColumnSearch
                        title="وضعیت"
                        values={filterValues}
                        onApply={applyColumnSearch}
                        fields={[
                            {
                                key: "state",
                                placeholder: "وضعیت",
                                type: "select",
                                options: orderStates,
                            },
                        ]}
                    />
                ),
                dataIndex: "state",
                key: "state",
                width: 135,
                render: (state: Enum) => (
                    <Badge
                        text={state.id === 3 ? "موفق" : state.title}
                        type={getOrderStatusBadgeType(state.id)}
                    />
                ),
            },
            {
                title: "کارمزد",
                dataIndex: "commission",
                key: "commission",
                align: "left",
                width: 130,
                render: (commission?: number) =>
                    commission == null
                        ? "-"
                        : `${digitSperator(commission)} تومان`,
            },
            {
                title: "مقدار دلاری",
                dataIndex: "inDollarAmount",
                key: "inDollarAmount",
                align: "left",
                width: 135,
                render: (amount?: number) =>
                    amount == null ? "-" : `${digitSperator(amount)} واحد`,
            },
            {
                title: "عملیات",
                key: "actions",
                fixed: "left",
                width: 120,
                render: (_, row) => (
                    <div className="actions-cell">
                        <Tooltip title="جزئیات سفارش">
                            <button
                                type="button"
                                className="table-action-button"
                                aria-label="جزئیات سفارش"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setIsOpen(true);
                                }}
                            >
                                <InfoCircleOutlined />
                            </button>
                        </Tooltip>
                        <Tooltip title="تغییر وضعیت سفارش">
                            <button
                                type="button"
                                className="table-action-button"
                                aria-label="تغییر وضعیت سفارش"
                                onClick={() => {
                                    setSelectedItem(row);
                                    setIsEditOpen(true);
                                }}
                            >
                                <EditOutlined />
                            </button>
                        </Tooltip>
                    </div>
                ),
            },
        ],
        [
            applyColumnSearch,
            filterValues,
            marketTypes,
            orderSides,
            orderSources,
            orderStates,
            orderPlatforms,
            orderTypes,
            ordersCount?.pageIndex,
            pageSize,
        ],
    );

    return (
        <Page className="pmx-orders" title="مدیریت سفارشات">
            {/* اسپینر فقط روی خود جدول است (prop `loading` در AntdTable)؛
                blur روی Card باعث می‌شد دو اسپینر هم‌زمان روی هم بیفتند. */}
            <Card title="سفارشات">
                {selectedItem && (
                    <OrderDetailModal
                        isOpen={isOpen}
                        setIsOpen={setIsOpen}
                        id={selectedItem.id}
                    />
                )}
                {selectedItem && isEditOpen && (
                    <OrderStateEditModal
                        isOpen={isEditOpen}
                        setIsOpen={setIsEditOpen}
                        item={selectedItem}
                        orderStates={editableOrderStates}
                        onSuccess={reloadOrders}
                    />
                )}
                <div className="table-toolbar">
                    <Button
                        className="export-btn"
                        text="خروجی اکسل"
                        type={buttonType.info}
                        icon={<ExportOutlined />}
                        onClick={() =>
                            downloadCSV(
                                "سفارشات",
                                buildOrdersCsvRows(orderList?.items ?? []),
                            )
                        }
                    />
                </div>
                <AntdTable<Order>
                    className="orders-table"
                    rowKey="id"
                    columns={columns}
                    dataSource={orderList?.items ?? []}
                    loading={cardLoading}
                    locale={{
                        emptyText: <Empty description="رکوردی یافت نشد" />,
                    }}
                    scroll={{ x: 2025 }}
                    pagination={{
                        current: ordersCount?.pageIndex ?? 1,
                        pageSize: ordersCount?.pageSize ?? pageSize,
                        total: ordersCount?.totalCount ?? 0,
                        showSizeChanger: true,
                        pageSizeOptions: [10, 20, 50, 100, 1000, 10000, 100000],
                        showTotal: (total, range) =>
                            `نمایش ${digitSperator(range[0])} تا ${digitSperator(range[1])} از ${digitSperator(total)} رکورد`,
                    }}
                    onChange={handleTableChange}
                />
            </Card>
        </Page>
    );
};
